// 导出所有类型
export * from './types';

// 导出核心类
export { ContextBuilder } from './context-builder';
export { ContextOptimizer } from './context-optimizer';
export { ContextCompressor } from './context-compressor';
export { DynamicToolCaller } from './tool-caller';

// 导出 Memory 集成
export { MemoryIntegration, MemoryIntegrationConfig } from './memory-integration';
export { 
  createContextBuilder, 
  createMemoryEnhancedContextBuilder, 
  createBasicContextBuilder,
  CreateContextBuilderOptions 
} from './context-factory';

// 导出工厂函数
import { ContextBuilder } from './context-builder';
import { ContextOptimizer } from './context-optimizer';
import { ContextCompressor } from './context-compressor';
import { DynamicToolCaller } from './tool-caller';
import { KnowledgeBase } from '../knowledge-base';
import { WorkflowManager } from '../workflow-manager';
import { TaskAnalyzer } from '../task-analyzer';

export interface ContextEngineConfig {
  // ContextBuilder 配置
  maxSize?: number;
  maxItems?: number;
  compressionThreshold?: number;
  relevanceThreshold?: number;
  cacheTTL?: number;
  enableCompression?: boolean;
  enableCache?: boolean;
  enableOptimization?: boolean;
  
  // Optimizer 配置
  optimizationStrategy?: string;
  preserveTypes?: string[];
  
  // Compressor 配置
  compressionStrategy?: string;
  targetCompressionRatio?: number;
  
  // ToolCaller 配置
  maxConcurrentCalls?: number;
  toolTimeout?: number;
  enableParallelTools?: boolean;
}

// 上下文引擎
export class ContextEngine {
  public readonly builder: ContextBuilder;
  public readonly optimizer: ContextOptimizer;
  public readonly compressor: ContextCompressor;
  public readonly toolCaller: DynamicToolCaller;
  
  constructor(
    knowledgeBase: KnowledgeBase,
    workflowManager: WorkflowManager,
    taskAnalyzer: TaskAnalyzer,
    config: ContextEngineConfig = {}
  ) {
    // 创建组件
    this.builder = new ContextBuilder(
      knowledgeBase,
      workflowManager,
      taskAnalyzer,
      {
        maxSize: config.maxSize,
        maxItems: config.maxItems,
        compressionThreshold: config.compressionThreshold,
        relevanceThreshold: config.relevanceThreshold,
        cacheTTL: config.cacheTTL,
        enableCompression: config.enableCompression,
        enableCache: config.enableCache,
        enableOptimization: config.enableOptimization
      }
    );
    
    this.optimizer = new ContextOptimizer({
      maxSize: config.maxSize,
      maxItems: config.maxItems,
      relevanceThreshold: config.relevanceThreshold,
      strategy: config.optimizationStrategy as any,
      preserveTypes: config.preserveTypes as any
    });
    
    this.compressor = new ContextCompressor({
      strategy: config.compressionStrategy as any,
      targetRatio: config.targetCompressionRatio
    });
    
    this.toolCaller = new DynamicToolCaller(
      this.builder,
      {
        maxConcurrentCalls: config.maxConcurrentCalls,
        defaultTimeout: config.toolTimeout,
        enableParallel: config.enableParallelTools
      }
    );
    
    // 集成优化器和压缩器
    this.integrateComponents();
  }
  
  /**
   * 构建上下文的便捷方法
   */
  async build(input: Parameters<ContextBuilder['build']>[0]) {
    return this.builder.build(input);
  }
  
  /**
   * 优化上下文的便捷方法
   */
  async optimize(context: Parameters<ContextOptimizer['optimize']>[0]) {
    return this.optimizer.optimize(context);
  }
  
  /**
   * 压缩上下文的便捷方法
   */
  async compress(context: Parameters<ContextCompressor['compress']>[0]) {
    return this.compressor.compress(context);
  }
  
  /**
   * 基于上下文调用工具的便捷方法
   */
  async callTools(
    context: Parameters<DynamicToolCaller['callWithContext']>[0],
    task: Parameters<DynamicToolCaller['callWithContext']>[1],
    strategy?: Parameters<DynamicToolCaller['callWithContext']>[2]
  ) {
    return this.toolCaller.callWithContext(context, task, strategy);
  }
  
  /**
   * 集成组件
   */
  private integrateComponents() {
    // 在 ContextBuilder 中注入优化和压缩逻辑
    const originalBuild = this.builder.build.bind(this.builder);
    
    this.builder.build = async (input) => {
      let context = await originalBuild(input);
      
      // 自动优化
      if (this.builder['config'].enableOptimization) {
        context = await this.optimizer.optimize(context);
      }
      
      // 自动压缩
      if (this.builder['config'].enableCompression &&
          context.totalSize > this.builder['config'].compressionThreshold) {
        context = await this.compressor.compress(context);
      }
      
      return context;
    };
  }
  
  /**
   * 创建默认的上下文引擎
   */
  static createDefault(
    knowledgeBase: KnowledgeBase,
    workflowManager: WorkflowManager,
    taskAnalyzer: TaskAnalyzer
  ): ContextEngine {
    return new ContextEngine(
      knowledgeBase,
      workflowManager,
      taskAnalyzer,
      {
        maxSize: 100 * 1024,        // 100KB
        maxItems: 100,
        compressionThreshold: 50 * 1024,
        relevanceThreshold: 0.3,
        enableCompression: true,
        enableCache: true,
        enableOptimization: true,
        optimizationStrategy: 'hybrid',
        compressionStrategy: 'hybrid',
        targetCompressionRatio: 0.5,
        enableParallelTools: true
      }
    );
  }
}